import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";

import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArticleIcon from "@mui/icons-material/Article";
import DescriptionIcon from "@mui/icons-material/Description";
import { Document, Page, pdfjs } from "react-pdf";
import axios from "axios";
import { nuclearn_api_url } from "src/App";
import { cleanArtifactFilename } from "src/utils/artifactUtils";
import { useVnvAssistStore } from "../store/VnvAssistStore";
import { useMultiClaimHighlight } from "../utils/claimHighlight";

pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js`;

const VnvAssistDocumentPanel = () => {
  const { selectedArtifact, claims, activeClaim } = useVnvAssistStore();

  const [file, setFile] = useState<Blob | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [containerWidth, setContainerWidth] = useState(350);

  const observerRef = useRef<ResizeObserver | null>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const syncedClaimIdRef = useRef<number | null>(null);

  // Callback ref to measure container width via ResizeObserver.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const updateWidth = () => {
      const computedStyle = window.getComputedStyle(node);
      const horizontalPadding =
        parseFloat(computedStyle.paddingLeft) +
        parseFloat(computedStyle.paddingRight);
      const horizontalBorder =
        parseFloat(computedStyle.borderLeftWidth) +
        parseFloat(computedStyle.borderRightWidth);
      setContainerWidth(
        Math.max(0, node.offsetWidth - horizontalPadding - horizontalBorder),
      );
    };
    updateWidth();

    observerRef.current = new ResizeObserver(updateWidth);
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    if (!selectedArtifact?.id) {
      setFile(null);
      return;
    }
    setIsFetching(true);
    setFetchError(null);
    setFile(null);
    setPageNumber(1);
    setNumPages(null);

    axios
      .get(
        `${nuclearn_api_url}/artifacts/${selectedArtifact.id}/download-document`,
        { responseType: "blob" },
      )
      .then((res) => setFile(res.data))
      .catch(() => setFetchError("Failed to load document."))
      .finally(() => setIsFetching(false));
  }, [selectedArtifact?.id]);

  // Jump to the active claim's page only when the claim itself changes.
  // Depending on pageNumber here would fight manual arrow-key / slider
  // navigation: changing pageNumber re-runs the effect, which sees the claim
  // still points to a different page and snaps back.
  useEffect(() => {
    if (!activeClaim) {
      syncedClaimIdRef.current = null;
      return;
    }
    if (syncedClaimIdRef.current === activeClaim.id) return;
    syncedClaimIdRef.current = activeClaim.id;
    if (activeClaim.page_number) {
      setPageNumber(activeClaim.page_number);
    }
  }, [activeClaim]);

  useMultiClaimHighlight(pageContainerRef, claims, activeClaim, pageNumber);

  if (!selectedArtifact) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        height="100%"
        spacing={1}
        sx={{ opacity: 0.4 }}
      >
        <DescriptionIcon sx={{ fontSize: 48 }} />
        <Typography variant="body2">No document selected</Typography>
      </Stack>
    );
  }

  const previousPage = () => setPageNumber((p) => Math.max(1, p - 1));
  const nextPage = () =>
    setPageNumber((p) => (numPages ? Math.min(numPages, p + 1) : p));

  return (
    <Box
      ref={containerRef}
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        px: 2,
        pt: 1,
        boxSizing: "border-box",
      }}
    >
      <Stack spacing={2} sx={{ height: "100%" }}>
        {/* Header */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ minWidth: 0 }}
          >
            <ArticleIcon color="primary" sx={{ flexShrink: 0 }} />
            <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
              {cleanArtifactFilename(selectedArtifact.filename)}
            </Typography>
          </Stack>
        </Box>

        {/* Document viewer */}
        <Box
          ref={pageContainerRef}
          sx={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {isFetching && (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {fetchError && (
            <Alert severity="error" sx={{ m: 2 }}>
              {fetchError}
            </Alert>
          )}
          {file && !fetchError && (
            <Document
              file={file}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              loading={
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              }
              noData={
                <Alert severity="warning">No document data was found.</Alert>
              }
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  border: "1px solid #e4e7eb",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  borderRadius: 1,
                  backgroundColor: "#f9f9f9",
                  mb: 2,
                }}
              >
                <Page
                  pageNumber={pageNumber}
                  width={containerWidth}
                  renderTextLayer
                  renderAnnotationLayer
                />
              </Box>
            </Document>
          )}
        </Box>

        {/* Page controls */}
        {numPages && numPages > 0 && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: "divider",
              pt: 2,
              pb: 1,
              backgroundColor: "background.paper",
            }}
          >
            <Stack spacing={1}>
              <input
                type="range"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => setPageNumber(Number(e.target.value))}
                style={{ width: "100%" }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <IconButton
                  onClick={previousPage}
                  disabled={pageNumber <= 1}
                  size="small"
                  color="primary"
                >
                  <ArrowBackIosIcon fontSize="small" />
                </IconButton>
                <Typography variant="body2">
                  Page {pageNumber} of {numPages}
                </Typography>
                <IconButton
                  onClick={nextPage}
                  disabled={pageNumber >= numPages}
                  size="small"
                  color="primary"
                >
                  <ArrowForwardIosIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default VnvAssistDocumentPanel;
